import { useEffect } from "react";

export class ExternalMobileAuth {
  static hasFlag(): boolean {
    return ExternalMobileAuth.hasUrlOrLocalStorageFlag();
  }
  static hasUrlOrLocalStorageFlag(): boolean {
    return (
      ExternalMobileAuth.hasUrlParamsFlag() ||
      ExternalMobileAuth.hasLocalStorageFlag()
    );
  }

  /**
   * Provider-level signal: should we consider this request part of an external mobile auth flow
   * for LoginProvider gating? Only URL and cookie are authoritative here to avoid false positives
   * from a stale localStorage sticky.
   */
  static isProviderExternalFlowActive(): boolean {
    return (
      ExternalMobileAuth.hasUrlParamsFlag() ||
      ExternalMobileAuth.hasCookieFlag()
    );
  }

  static hasCookieFlag(): boolean {
    return (
      typeof document !== "undefined" &&
      document.cookie
        .split(";")
        .some((c) => c.trim().startsWith("externalMobileAuth=1"))
    );
  }
  static hasUrlParamsFlag(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get("externalMobileAuth") === "1";
  }

  static hasLocalStorageFlag(): boolean {
    return (
      ExternalMobileAuth.readFromLocalStorage("externalMobileAuth") === "1"
    );
  }
  static setLocalStorageFlag() {
    typeof window !== "undefined" &&
      window.localStorage?.setItem("externalMobileAuth", "1");
  }

  static setUrlParamsFlag(searchParams: URLSearchParams): URLSearchParams {
    searchParams.set("externalMobileAuth", "1");
    return searchParams;
  }

  static removeLocalStorageFlag() {
    typeof window !== "undefined" &&
      window.localStorage?.removeItem("externalMobileAuth");
  }
  static clearCookie() {
    try {
      fetch("/identity/clear-external-mobile-auth", {
        credentials: "include",
        cache: "no-store",
      }).catch(() => {
        // noop
      });
    } catch {
      // noop
    }
  }

  private static readFromLocalStorage(key: string) {
    try {
      return typeof window !== "undefined"
        ? window.localStorage?.getItem(key)
        : null;
    } catch (e) {
      console.warn("Could not read loginStrategy from localStorage", e);
      return null;
    }
  }
}

export const usePromoteCookieFlagToLocalStorageEffect = () => {
  useEffect(() => {
    if (
      ExternalMobileAuth.hasCookieFlag() &&
      !ExternalMobileAuth.hasLocalStorageFlag()
    ) {
      ExternalMobileAuth.setLocalStorageFlag();
    }
  }, []);
};
